import * as React from 'react';
import { Toast as FlowBiteToast } from 'flowbite-react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { cn, ToastMessageContextProps, ToastType } from '@restworld/utils-common';
import InteractiveToast from './toast.interactive';
import { Alert, AlertProps } from '@restworld/ui-ds-base';

export const Toast = ({
  message,
  visible = false,
  type = 'success',
  ui = 'simple',
  title,
  position = 'top',
}: ToastMessageContextProps) =>
  visible ? (
    <ToastStyled {...{ type, position, ui }}>
      {ui === 'interactive' ? (
        <InteractiveToast type={type} message={message} title={title} />
      ) : ui === 'alert' ? (
        <Alert variant={type as AlertProps['variant']} text={message} />
      ) : (
        <>
          <IconWrapper>
            <CheckIconStyled />
          </IconWrapper>
          <ToastMessage>{message}</ToastMessage>
          <FlowBiteToast.Toggle />
        </>
      )}
    </ToastStyled>
  ) : null;
export default Toast;
const ToastStyled = ({
  type,
  position,
  ui,
  className,
  ...props
}: Pick<ToastMessageContextProps, 'type' | 'position' | 'ui'> & React.ComponentProps<typeof FlowBiteToast>) => (
  <FlowBiteToast
    className={cn(
      'z-[99] fixed right-24 w-full',
      position === 'top' && 'top-24',
      position === 'bottom' && 'bottom-24',
      ui !== 'alert' && typeStyleMap[type as ToastType],
      ui === 'alert' &&
        'lg:bottom-24 lg:right-24 lg:min-w-fit min-w-[312px] w-fit bottom-4 right-[50%] lg:translate-x-0 translate-x-[50%] shadow-none bg-transparent',
      className
    )}
    {...props}
  />
);
const IconWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', className)} {...props} />
);
const CheckIconStyled = ({ className, ...props }: React.SVGProps<SVGSVGElement>) => (
  <CheckIcon className={cn('h-5 w-5', className)} {...props} />
);
const ToastMessage = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('ml-3 text-sm font-normal', className)} {...props} />
);
export const typeStyleMap: { [K in ToastType]: string } = {
  success: 'bg-green-100 text-green-500',
  fail: 'bg-red-100 text-red-500',
  info: 'bg-blue-100 text-blue-500',
  warning: 'bg-yellow-100 text-yellow-500',
};
